import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import type { ContextHint } from "@/lib/api";
import { formatShortDate } from "@/lib/context-checks";
import type { ContextAnswerReview as ReviewItem } from "@/lib/context-hint-review";
import { colors, fonts, iconTints, radius, spacing } from "@/lib/theme";

export function ContextAnswerReview({
    items,
    saving,
    onDismiss,
    onQuestion,
    onSource,
    inline = false,
    onDismissItem
}: {
    items: ReviewItem[];
    saving: boolean;
    onDismiss: () => void;
    onQuestion?: (id: string) => void;
    inline?: boolean;
    onDismissItem?: (key: string) => void;
    onSource: (hint: ContextHint) => void;
}) {
    const questions = [...new Set(items.map((item) => item.questionId))];
    return (
        <View
            style={[styles.box, inline && styles.inlineBox]}
            testID={inline ? "context-question-review" : "context-completion-review"}
        >
            {!inline ? (
                <>
                    <Text style={styles.title} accessibilityRole="header">
                        Review answers
                    </Text>
                    <Text style={styles.intro}>
                        Based on our existing patient information, some answers may need to be
                        selected or deselected.
                    </Text>
                </>
            ) : null}
            {questions.map((id) => {
                const suggestions = items.filter((item) => item.questionId === id);
                return (
                    <View key={id} style={styles.question}>
                        {!inline ? (
                            <Pressable
                                style={styles.questionHeading}
                                onPress={() => onQuestion?.(id)}
                                accessibilityLabel={`Go to question: ${suggestions[0].questionText}`}
                                accessibilityRole="button"
                                accessibilityHint="Go to this question to edit your answer"
                            >
                                <Text style={styles.questionText}>
                                    {suggestions[0].questionText}
                                </Text>
                                <MaterialCommunityIcons
                                    name="arrow-up-right"
                                    size={22}
                                    color={colors.info600}
                                />
                            </Pressable>
                        ) : null}
                        {suggestions.map((item) => (
                            <View key={item.key} style={styles.item}>
                                {inline ? (
                                    <Text style={styles.intro}>
                                        Based on existing patient information
                                    </Text>
                                ) : null}
                                <Text style={styles.answer}>
                                    <Text style={styles.action}>{item.action}: </Text>
                                    {item.label}
                                    {item.conflict ? " · Sources disagree" : ""}
                                </Text>
                                <View style={styles.sources}>
                                    {item.sources.map((hint, index) => {
                                        const source =
                                            hint.sourceType === "questionnaire"
                                                ? "Patient questionnaire"
                                                : hint.sourceLabel;
                                        const date = formatShortDate(hint.observedAt);
                                        return (
                                            <Pressable
                                                key={index}
                                                disabled={hint.sourceViewEnabled === false}
                                                onPress={() => onSource(hint)}
                                                accessibilityRole="button"
                                                accessibilityLabel={`Open source: ${source}`}
                                            >
                                                <Text style={styles.source}>
                                                    {source}
                                                    {date && !source.endsWith(date)
                                                        ? ` · ${date}`
                                                        : ""}
                                                </Text>
                                            </Pressable>
                                        );
                                    })}
                                </View>
                                {inline ? (
                                    <Pressable
                                        disabled={saving}
                                        onPress={() => onDismissItem?.(item.key)}
                                        accessibilityRole="button"
                                        accessibilityLabel={`Dismiss ${item.action.toLowerCase()}: ${item.label}`}
                                        style={[styles.dismiss, saving && styles.dismissDisabled]}
                                    >
                                        <Text style={styles.dismissText}>Dismiss</Text>
                                    </Pressable>
                                ) : null}
                            </View>
                        ))}
                    </View>
                );
            })}
            {!items.length ? (
                <Text style={styles.intro}>These suggestions have been addressed.</Text>
            ) : null}
            {!inline ? (
                <Pressable
                    testID="dismiss-context-review"
                    disabled={saving}
                    onPress={onDismiss}
                    accessibilityRole="button"
                    style={[styles.dismiss, saving && styles.dismissDisabled]}
                >
                    <Text style={styles.dismissText}>
                        {saving ? "Saving…" : "Dismiss and continue"}
                    </Text>
                </Pressable>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    box: {
        marginTop: spacing.lg,
        marginHorizontal: spacing.lg,
        padding: spacing.md,
        gap: spacing.sm,
        backgroundColor: iconTints.sky.bg,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: iconTints.sky.bg
    },
    inlineBox: {
        marginHorizontal: 0,
        marginTop: spacing.sm
    },
    questionHeading: { flexDirection: "row", alignItems: "center", gap: spacing.sm, minHeight: 44 },
    title: { fontFamily: fonts.semiBold, fontSize: 15, color: colors.textPrimary },
    intro: { fontFamily: fonts.regular, fontSize: 12, lineHeight: 18, color: colors.textPrimary },
    question: { gap: spacing.xs, paddingVertical: spacing.xs },
    questionText: {
        flex: 1,
        fontFamily: fonts.semiBold,
        fontSize: 13,
        lineHeight: 19,
        color: colors.textPrimary,
        textDecorationLine: "underline"
    },
    item: { gap: 2 },
    answer: { fontFamily: fonts.regular, fontSize: 12, lineHeight: 18, color: colors.textPrimary },
    action: { fontFamily: fonts.semiBold },
    sources: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
    source: {
        fontFamily: fonts.regular,
        fontSize: 11,
        lineHeight: 17,
        color: colors.info600,
        textDecorationLine: "underline"
    },
    dismiss: {
        alignSelf: "stretch",
        alignItems: "center",
        backgroundColor: colors.surface,
        borderRadius: radius.sm,
        marginTop: spacing.sm,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        minHeight: 44,
        justifyContent: "center"
    },
    dismissDisabled: { opacity: 0.6 },
    dismissText: { fontFamily: fonts.semiBold, fontSize: 13, color: colors.info600 }
});
